import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ImageViewer = ({ image, annotations, viewerMode, onViewerModeChange, showAnnotationTools }) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [showMeasurements, setShowMeasurements] = useState(true);

  const handleZoomIn = () => setZoom(Math.min(zoom + 25, 400));
  const handleZoomOut = () => setZoom(Math.max(zoom - 25, 25));
  const handleRotateLeft = () => setRotation((rotation - 90) % 360);
  const handleRotateRight = () => setRotation((rotation + 90) % 360);
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
    setBrightness(100);
    setContrast(100);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Viewer Controls */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {image?.category} - {image?.toothNumber}
          </span>
          <span className="text-xs text-muted-foreground">
            {image?.date} at {image?.time}
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            variant={viewerMode === '2d' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => onViewerModeChange('2d')}
          >
            2D
          </Button>
          {image?.type === 'ct' && (
            <>
              <Button
                variant={viewerMode === '3d' ? 'default' : 'ghost'}
                size="xs"
                onClick={() => onViewerModeChange('3d')}
              >
                3D
              </Button>
              <Button
                variant={viewerMode === 'ct' ? 'default' : 'ghost'}
                size="xs"
                onClick={() => onViewerModeChange('ct')}
              >
                CT Slices
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Image Display Area */}
      <div className="flex-1 relative bg-gray-900 overflow-hidden">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease'
          }}
        >
          <AppImage
            src={image?.thumbnail}
            alt={image?.thumbnailAlt}
            className="max-w-full max-h-full object-contain"
            style={{
              filter: `brightness(${brightness}%) contrast(${contrast}%)`
            }}
          />
          
          {/* Annotation Overlays */}
          {showMeasurements && annotations?.length > 0 && (
            <div className="absolute inset-0">
              {annotations?.map((annotation) => (
                <div
                  key={annotation?.id}
                  className="absolute"
                  style={{
                    left: `${annotation?.x}%`,
                    top: `${annotation?.y}%`
                  }}
                >
                  {annotation?.type === 'marker' && (
                    <div className="w-4 h-4 bg-red-500 rounded-full border-2 border-white" />
                  )}
                  {annotation?.type === 'measurement' && (
                    <div className="flex items-center gap-1">
                      <div className="h-px w-12 bg-yellow-400" />
                      <span className="text-xs text-yellow-400 font-medium bg-black/50 px-1 rounded">
                        {annotation?.value}mm
                      </span>
                    </div>
                  )}
                  {annotation?.type === 'note' && (
                    <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded shadow-lg max-w-32">
                      {annotation?.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Viewer Info Overlay */}
        <div className="absolute top-4 left-4 bg-black/70 text-white text-xs px-3 py-2 rounded space-y-1">
          <div>Zoom: {zoom}%</div>
          <div>Rotation: {rotation}°</div>
          {image?.type === 'ct' && viewerMode === 'ct' && (
            <div>Slice: 45/120</div>
          )}
        </div>

        {/* Annotation Count */}
        {annotations?.length > 0 && (
          <div className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs px-3 py-2 rounded flex items-center gap-2">
            <Icon name="MessageSquare" size={14} />
            <span>{annotations?.length} annotations</span>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="flex items-center justify-between p-3 border-t border-border bg-muted/30">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconName="ZoomOut"
            onClick={handleZoomOut}
            disabled={zoom <= 25}
          />
          <span className="text-sm font-medium text-foreground min-w-16 text-center">
            {zoom}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconName="ZoomIn"
            onClick={handleZoomIn}
            disabled={zoom >= 400}
          />
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconName="RotateCcw"
            onClick={handleRotateLeft}
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="RotateCw"
            onClick={handleRotateRight}
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="Maximize2"
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            onClick={handleReset}
          />
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconName="Download"
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="Share2"
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="Printer"
          />
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;